import ContentLoader from 'react-content-loader';

import { Entry } from 'contentful';
import { format } from 'date-fns';
import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { useClientHasRendered } from '../../../helpers/hooks';
import {
  ContentfulResizeTypes,
  getContentfulLoader,
} from '../../../helpers/imageLoaders/contentful';
import { getBlogPostUrl } from '../../../helpers/urls';
import {
  TypeBlogTagSkeleton,
  TypeCountrySkeleton,
  TypeUsRegionSkeleton,
} from '../../../interfaces/contentful';
import { EventMykissCardClicked } from '../../../interfaces/events';
import { LinkCard } from '../../Cards/LinkCard/LinkCard';
import { Pill } from '../../Clickables/Pill/Pill';
import { Heading } from '../../Heading/Heading';
import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import { Text } from '../../Text/Text';

interface IProps {
  blogPostDescription: string;
  blogPostTitle: string;
  blogTags?: (Entry<TypeBlogTagSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | undefined)[];
  categorySlug: string;
  className?: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  country?: Entry<TypeCountrySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | undefined;
  innerClassName?: string;
  imageUrl?: string;
  publishDate?: string;
  selectedRegion?: Entry<TypeUsRegionSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | undefined;
  slug: string;
}

const RANDOM_BLOGPOST_CARD_KEY = 'fishbrain-blog-post-card-v2-unique-key-12345';

export const BlogPostCarouselCard = ({
  blogPostDescription,
  blogPostTitle,
  blogTags,
  categorySlug,
  className,
  clickEventSource,
  clickEventSourceId,
  country,
  innerClassName,
  imageUrl,
  publishDate,
  selectedRegion,
  slug,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const clientHasRendered = useClientHasRendered();
  const countryName = country?.fields.countryName ?? undefined;

  const formattedPublishDate =
    clientHasRendered && publishDate ? format(new Date(publishDate), 'MMMM do, yyyy') : undefined;
  const tagList = blogTags?.map(tag => tag);
  const IMAGEWIDTH = 250;
  const IMAGEHEIGTH = 250;

  const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getContentfulLoader(src, width, IMAGEWIDTH, IMAGEHEIGTH, {
      quality,
      resize: ContentfulResizeTypes.FILL,
    });

  return (
    <LinkCard
      to={getBlogPostUrl(categorySlug, slug)}
      eventProperties={{
        source: clickEventSource,
        target: 'blog',
        target_id: slug,
        source_id: clickEventSourceId,
        page: currentPage,
      }}
      className={`flex flex-row-reverse gap-1 rounded-lg bg-sea-foam sm:gap-4 md:flex-row ${className}`}
    >
      <div className={twMerge('flex w-1/4 justify-end sm:w-1/3', innerClassName)}>
        <div className="relative aspect-square w-full">
          {imageUrl ? (
            <NextImageWrapper
              wrapperClassName="size-full"
              customPlaceholderClassName="rounded-r-lg md:rounded-l-lg md:rounded-r-none"
              imageClassName="size-full aspect-square rounded-r-lg object-cover md:rounded-l-lg md:rounded-r-none"
              alt={blogPostTitle}
              src={imageUrl}
              loader={contentfulLoader}
              width={IMAGEHEIGTH}
              height={IMAGEHEIGTH}
            />
          ) : (
            <div className="size-full rounded-full bg-lake-fog" />
          )}
        </div>
      </div>
      <div className="w-3/4 py-4 pl-4 sm:w-2/3 md:pl-0 md:pr-4">
        <Heading as="h4" variant="2xs">
          {blogPostTitle}
        </Heading>
        {formattedPublishDate ? (
          <Text variant="sm" className="text-midnight">
            {formattedPublishDate}
          </Text>
        ) : (
          <ContentLoader height={24} uniqueKey={RANDOM_BLOGPOST_CARD_KEY} />
        )}
        <Text variant="sm" className="mb-2 line-clamp-3 text-ellipsis font-normal">
          {blogPostDescription}
        </Text>

        <div className="flex w-full flex-wrap gap-2">
          {countryName && (
            <Pill variant="infoClown" size="sm" disabled>
              {countryName}
            </Pill>
          )}
          {selectedRegion && (
            <Pill variant="infoClown" size="sm" disabled>
              {selectedRegion.fields.regionName}
            </Pill>
          )}
          {tagList?.map(tag => {
            if (!tag) {
              return <></>;
            }
            const { tagName } = tag.fields;
            const { id } = tag.sys;
            return (
              <Pill as="div" variant="infoBlue" size="sm" disabled key={id}>
                {tagName}
              </Pill>
            );
          })}
        </div>
      </div>
    </LinkCard>
  );
};
